import type { Shape } from './en';
import type { catalogsDe } from './catalogs.de';

export const catalogsEn: Shape<typeof catalogsDe> = {
  title: 'Catalogs',
  description:
    'Choice lists of the company with their properties. Forms offer active entries only.',
  count_one: '{{count}} entry',
  count_other: '{{count}} entries',
  edit: 'Edit “{{name}}”',
  none: 'None',
  choose: 'Please choose',
  inactive: 'Inactive',
  default: 'Default',
  deletedEntry: 'Deleted entry',
  yes: 'Yes',
  no: 'No',
  colors: {
    none: 'No colour',
    blue: 'Blue',
    sky: 'Sky blue',
    teal: 'Teal',
    green: 'Green',
    lime: 'Lime',
    amber: 'Amber',
    orange: 'Orange',
    red: 'Red',
    rose: 'Rose',
    violet: 'Violet',
    slate: 'Slate',
  },
  fields: {
    name: 'Name',
    code: 'Code',
    color: 'Colour',
    description: 'Description',
    isDefault: 'Default',
    active: 'Active',
    status: 'Status',
  },
  page: {
    search: 'Search entries',
    searchPlaceholder: 'Name, code or text',
    status: 'Status',
    statuses: { active: 'Active', inactive: 'Inactive' },
    allOf: '{{field}}: all',
    withoutValue: 'Not set',
    new: 'New entry',
    empty: 'No entries yet',
    emptyHint: 'Create entries, import them from a file or load the defaults.',
    emptyReadOnly: 'Entries are created by people who may change the settings of the module.',
    noMatches: 'No entries found',
    noMatchesHint: 'Change the search or remove filters.',
    range: '{{from}}–{{to}} of {{total}}',
    moveUp: 'Move “{{name}}” up',
    moveDown: 'Move “{{name}}” down',
    moved: '“{{name}}” is now at position {{position}} of {{count}}.',
    remove: 'Delete “{{name}}”',
    removeTitle: 'Delete “{{name}}”?',
    removeDescription:
      'The entry is no longer offered. Records that use it keep showing it. To only stop offering it, set it to inactive.',
    removed: '“{{name}}” deleted',
  },
  dialog: {
    newTitle: 'New entry',
    editTitle: 'Edit entry',
    defaultHint: 'Preselected in forms. Only one entry can be the default.',
    activeHint: 'Inactive entries are no longer offered. Records that use them keep showing them.',
    created: '“{{name}}” created',
    saved: '“{{name}}” saved',
  },
  import: {
    action: 'Import',
    title: 'Import entries',
    description:
      'A JSON file with a list of entries, at most 1 MB. Entries are matched by name: new names are created, existing entries take the values from the file.',
    choose: 'Choose file',
    chosen: 'File: {{name}}',
    deactivateMissing: 'Deactivate entries that are not in the file',
    checking: 'Checking the file …',
    preview: 'The import will do the following:',
    counts: {
      created: 'New',
      updated: 'Changed',
      unchanged: 'Unchanged',
      deactivated: 'Deactivated',
      unresolved: 'References without match',
      skipped: 'Skipped',
    },
    countsHint:
      'References to entries the other catalog does not have stay empty. New entries missing a required reference are skipped.',
    submit: 'Import',
    done: 'Import finished: {{created}} new, {{updated}} changed, {{deactivated}} deactivated.',
    invalidFile: 'The file does not hold a list of entries in JSON format.',
    tooLarge: 'The file is too large. At most 1 MB is possible.',
    invalidEntry: 'Entry {{number}}: {{message}}',
    invalidValue: 'Invalid value ({{field}})',
  },
  defaults: {
    load: 'Load defaults',
    title: 'Load defaults?',
    description:
      'Creates the entries of the defaults that do not exist yet. Existing entries stay as they are.',
    confirm: 'Load',
    done_one: '{{count}} entry created',
    done_other: '{{count}} entries created',
    nothing: 'All defaults are already there.',
    unresolved_one: '{{count}} reference found no matching entry and stayed empty.',
    unresolved_other: '{{count}} references found no matching entry and stayed empty.',
    skipped_one: '{{count}} entry is missing because its required reference found no match.',
    skipped_other: '{{count}} entries are missing because their required reference found no match.',
  },
  errors: {
    exists: 'An entry with this name already exists.',
    limit:
      'The catalog has reached the maximum number of entries. Delete entries you no longer need to make room.',
    reference: 'This entry no longer exists. Please choose another one.',
    required: 'Required',
    inactiveDefault: 'An inactive entry cannot be the default.',
    duplicateName: 'The name appears more than once in the file.',
    multipleDefaults: 'Only one entry can be the default.',
    number: 'Please enter a number.',
    orderChanged: 'The list has changed in the meantime. It now shows the current state.',
  },
};
